import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wind, PlusCircle, FilterIcon, ArrowLeft, Building2 } from 'lucide-react';
import { useSearchParams, Link } from 'react-router-dom';
import AppliedFilters from '@/components/AppliedFilters';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import DynamicFormModal from '@/components/DynamicFormModal';
import { useToast } from '@/components/ui/use-toast';
import { dryersColumns, columnsExcel } from '@/pages/parametrizacion/dryers/utils/dryersColumns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { fetchDryers, createDryer, updateDryer, deleteDryer, exportDryers } from '@/pages/parametrizacion/dryers/Services/dryers.services';
import FilterDrawer from '@/components/FilterDrawer';
import ExportExcelButton from '@/components/ExportExcelButton';

const ITEMS_PER_PAGE = 10;
const DEFAULT_FILTERS = { estado: 'Activa' };

const DryersPage = () => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const idLavanderia = searchParams.get('lavanderia');
  const [laundryName, setLaundryName] = useState('');

  const [displayedData, setDisplayedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState(
    idLavanderia ? { ...DEFAULT_FILTERS, 'lavanderia.id': idLavanderia } : DEFAULT_FILTERS
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [modalMode, setModalMode] = useState('create');

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [laundryOptionsModal, setLaundryOptionsModal] = useState([]);
  const [laundryOptionsFilter, setLaundryOptionsFilter] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { fetchLaundries } = await import('@/pages/parametrizacion/laundries/Services/laundries.services');
        const r = await fetchLaundries({ limit: 200, offset: 1, query: { estado: 'Activo' } });
        const data = r?.data || [];
        const optsModal = data.map(l => ({ value: { id: l._id, nombre: l.nombre }, label: l.nombre }));
        const optsFilter = data.map(l => ({ value: l._id, label: l.nombre }));
        if (!cancelled) {
          setLaundryOptionsModal(optsModal);
          setLaundryOptionsFilter(optsFilter);
        }
      } catch (e) {
        console.error('Error precargando lavanderías', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchAndSetDryers = useCallback(async () => {
    setIsLoading(true);
    try {
      const offset = currentPage;
      const { data, totalRecords } = await fetchDryers({ limit: ITEMS_PER_PAGE, offset, query: filters });
      setDisplayedData(data);
      setTotalItems(totalRecords);
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'No se pudieron obtener las secadoras.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, filters, toast]);

  useEffect(() => {
    fetchAndSetDryers();
  }, [fetchAndSetDryers]);

  // Fetch laundry name if coming from CentroParametrizacionPage
  useEffect(() => {
    if (idLavanderia) {
      const fetchLaundryName = async () => {
        try {
          const { fetchLaundryById } = await import('@/pages/parametrizacion/laundries/Services/laundries.services');
          const laundry = await fetchLaundryById(idLavanderia);
          setLaundryName(laundry?.nombre || '');
        } catch (e) {
          console.error('Error fetching laundry:', e);
        }
      };
      fetchLaundryName();
    }
  }, [idLavanderia]);

  const handlePageChange = (page) => setCurrentPage(page);

  const handleOpenModal = (mode, item = null) => {
    setModalMode(mode);
    setCurrentItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentItem(null);
  };

  const handleSaveItem = async (itemData) => {
    try {
      if (modalMode === 'create') {
        const response = await createDryer(itemData);
        if (response.code === 201) {
          toast({ title: 'Secadora creada', description: 'La secadora fue registrada correctamente.' });
        } else {
          throw new Error(response.message || 'Error al crear secadora');
        }
      } else if (modalMode === 'edit') {
        const response = await updateDryer(currentItem._id, itemData);
        if (response.code === 201) {
          toast({ title: 'Secadora actualizada', description: 'Los datos fueron actualizados correctamente.' });
        } else {
          throw new Error(response.message || 'Error al actualizar secadora');
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Ocurrió un problema al guardar la secadora.',
        variant: 'destructive',
      });
    } finally {
      handleCloseModal();
      fetchAndSetDryers();
    }
  };

  const handleDeleteConfirmation = (item) => {
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteItem = async () => {
    try {
      const response = await deleteDryer(itemToDelete._id);
      if (response.code === 200 || response.code === 201) {
        setDisplayedData(prev => prev.filter(row => row._id !== itemToDelete._id));
        setTotalItems(prev => Math.max(0, prev - 1));
        toast({
          title: 'Secadora eliminada',
          description: `La secadora "${itemToDelete?.nombre}" fue eliminada correctamente.`,
        });
        await fetchAndSetDryers();
      } else {
        throw new Error(response.message || 'Error al eliminar la secadora');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Ocurrió un problema al eliminar la secadora.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleAction = (actionType, row) => {
    if (actionType === 'edit') handleOpenModal('edit', row);
    else if (actionType === 'delete') handleDeleteConfirmation(row);
  };

  const handleFilterChange = (newFilters) => {
    setCurrentPage(1);
    setFilters(newFilters);
  };

  const handleRemoveFilter = (key) => {
    const newFilters = { ...filters };
    delete newFilters[key];
    setFilters(newFilters);
    setCurrentPage(1);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="p-4 md:p-6"
    >
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {idLavanderia && (
              <Button asChild variant="outline" size="icon" className="shrink-0">
                <Link to={`/parametrizacion/centros/${idLavanderia}`}>
                  <ArrowLeft className="h-5 w-5" />
                </Link>
              </Button>
            )}
            <div className="bg-primary/10 p-3 rounded-lg">
              <Wind className="h-8 w-8 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl">
                Módulo de Secadoras
                {laundryName && (
                  <span className="text-sm font-normal text-muted-foreground ml-2">
                    — {laundryName}
                  </span>
                )}
              </CardTitle>
              <CardDescription>
                {idLavanderia
                  ? 'Gestiona las secadoras del centro de lavado.'
                  : 'Gestiona las secadoras de la empresa.'}
              </CardDescription>
            </div>
          </div>

          <div className="w-full md:w-auto flex flex-col md:flex-row gap-3">
            <div className="flex flex-wrap gap-2 justify-end">
              <ExportExcelButton
                title="Secadoras"
                columns={columnsExcel}
                getData={() => exportDryers({ query: filters })}
              />
              <Button onClick={() => handleOpenModal('create')}>
                <PlusCircle className="mr-2 h-4 w-4" /> Crear Secadora
              </Button>
              <Button variant="outline" onClick={() => setIsDrawerOpen(true)} className="gap-2">
                <FilterIcon className="mr-2 h-4 w-4" />
                Filtros
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <div className="w-full">
            <AppliedFilters
              filters={filters}
              fields={[
                {
                  id: 'estado', label: 'Estado', type: 'select', options: [
                    { value: 'Activa', label: 'Activa' },
                    { value: 'Inactiva', label: 'Inactiva' },
                  ],
                },
                { id: 'lavanderia.id', label: 'Centro de Lavado', type: 'select', options: laundryOptionsFilter },
              ]}
              onRemoveFilter={handleRemoveFilter}
            />
          </div>

          <DataTable
            data={displayedData}
            columns={dryersColumns}
            isLoading={isLoading}
            onAction={handleAction}
            showCreateButton={false}
          />

          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={handlePageChange}
          />

          <FilterDrawer
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            fields={[
              {
                id: 'estado', label: 'Estado', type: 'select', options: [
                  { value: 'Activa', label: 'Activa' },
                  { value: 'Inactiva', label: 'Inactiva' },
                ],
              },
              {
                id: 'lavanderia.id',
                label: 'Centro de Lavado',
                type: 'select',
                options: laundryOptionsFilter,
              },
            ]}
            initialFilters={filters}
            onChange={handleFilterChange}
            onApply={handleFilterChange}
            initialDay={true}
          />
        </CardContent>
      </Card>

      <AnimatePresence>
        {isModalOpen && (
          <DynamicFormModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            onSave={handleSaveItem}
            item={currentItem}
            fields={dryersColumns.map(f => f.id === 'lavanderia' ? { ...f, options: laundryOptionsModal } : f)}
            title={modalMode === 'create' ? 'Crear Nueva Secadora' : 'Editar Secadora'}
            mode={modalMode}
          />
        )}
      </AnimatePresence>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro de eliminar esta secadora?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente la secadora
              "{itemToDelete?.nombre}" de tus registros.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setItemToDelete(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteItem} className="bg-destructive hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

export default DryersPage;
